import React, { useState } from 'react';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface SpecificationsSectionProps {
    project: any;
    getImageUrl: (url: string) => string;
}

const SpecificationsSection: React.FC<SpecificationsSectionProps> = ({ project, getImageUrl }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!project.specifications || project.specifications.length === 0) return null;

    const MOBILE_LIMIT = 3; // 3 rows of 1 column on mobile

    return (
        <div id="specifications" className="card border border-gray-200 shadow-sm rounded-2xl mb-4">
            <div className="bg-white py-4 px-6 md:px-8 border-b border-gray-200 flex items-center gap-2">
                <h4 className="text-xl md:text-2xl mb-0 font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Specifications</h4>
                <SectionInfoBox text={project.sectionInfo?.specifications} />
            </div>
            <div className="p-1 md:py-10 md:pr-10 md:pl-24">
                <div className="flex flex-wrap md:-mx-3">
                    {project.specifications.map((spec: any, i: number) => {
                        const isHiddenOnMobile = !isExpanded && i >= MOBILE_LIMIT;
                        return (
                            <div 
                                className={`w-full md:w-1/2 px-0 md:px-6 mb-2 md:mb-4 ${isHiddenOnMobile ? 'hidden md:block' : 'block'}`} 
                                key={i}
                            >
                                <div className="spec-item p-4 pl-5 md:p-6 bg-gray-50 rounded-2xl flex items-start gap-4 md:gap-6 h-full transition-all hover:shadow-md-sm border-gray-200">
                                    <div className="bg-white p-2 rounded-lg shadow-sm flex items-center justify-center" style={{ width: '50px', height: '50px', flexShrink: 0 }}>
                                        {spec.icon ? (
                                            <img 
                                                src={getImageUrl(spec.icon)} 
                                                style={{ width: '30px', height: '30px', objectFit: 'contain' }} 
                                                alt={spec.category} 
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    if (target.parentElement) {
                                                        const icon = document.createElement('i');
                                                        icon.className = 'fa-solid fa-list-check text-primary';
                                                        target.parentElement.appendChild(icon);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <i className="fa-solid fa-list-check text-primary"></i>
                                        )}
                                    </div>
                                    <div className="min-w-0">
                                        <h5 className="text-[14px] md:text-[15px] font-black text-[#05144b] mb-1">{spec.category}</h5>
                                        <p className="mb-0 text-[#05144b] font-medium text-[13px] md:text-[14px] leading-normal opacity-80">{spec.details}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {project.specifications.length > MOBILE_LIMIT && (
                    <div className="mt-6 flex justify-center md:hidden">
                        <button 
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] rounded-full text-[12px] font-black transition-all shadow-lg active:scale-95 border border-[#f1ae24]/30"
                        >
                            <span>{isExpanded ? 'View Less' : `View More (${project.specifications.length - MOBILE_LIMIT}+)`}</span>
                            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px]`}></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpecificationsSection;
